<template>
  <div class="mt-3 flex flex-col gap-3">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
      <div class="xl:col-span-2">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Select {{ labels.TEACHER }}</label>
        <Select v-model="selectedStaffId" :options="staffOptions" optionLabel="label" optionValue="value"
          placeholder="Choose a staff member..." size="small" class="w-full rounded-lg" filter />
      </div>
    </div>

    <div v-if="!selectedStaffId" class="text-center py-12 bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
      <i class="pi pi-user text-2xl text-gray-300 mb-2 block"></i>
      <p class="text-gray-500 text-sm font-medium">Select a staff member to view their weekly schedule across all {{ labels.CLASSROOM.toLowerCase() }}s.</p>
    </div>

    <div v-else-if="loadingTimetables" class="flex justify-center py-12">
      <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
    </div>

    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div class="bg-white p-2.5 rounded-xl border border-gray-200 text-center">
          <div class="text-lg font-bold text-primary">{{ teacherEntries.length }}</div>
          <div class="text-xs text-gray-500">Classes / Week</div>
        </div>
        <div class="bg-white p-2.5 rounded-xl border border-gray-200 text-center">
          <div class="text-lg font-bold text-primary-700">{{ uniqueSubjects.length }}</div>
          <div class="text-xs text-gray-500">{{ labels.SUBJECT }}s</div>
        </div>
        <div class="bg-white p-2.5 rounded-xl border border-gray-200 text-center">
          <div class="text-lg font-bold text-purple-800">{{ uniqueClassrooms.length }}</div>
          <div class="text-xs text-gray-500">{{ labels.CLASSROOM }}s</div>
        </div>
      </div>

      <div v-if="conflicts.length > 0"
        class="p-2.5 bg-red-50 border border-red-200 rounded-xl">
        <h3 class="font-semibold text-red-800 mb-1.5 flex items-center gap-1 text-sm">
          <i class="pi pi-exclamation-triangle text-xs"></i> Scheduling Conflicts
        </h3>
        <div v-for="(conflict, i) in conflicts" :key="i" class="text-xs text-red-700 mb-0.5">
          {{ conflict }}
        </div>
      </div>

      <div v-for="day in activeDays" :key="day" class="mb-1.5">
        <h3 class="font-semibold text-gray-700 mb-1.5 uppercase text-xs tracking-wider">{{ day }}</h3>
        <div v-if="(entriesByDay[day] || []).length === 0" class="text-xs text-gray-400 italic pl-2">
          No classes
        </div>
        <div v-else class="space-y-1.5">
          <div v-for="entry in entriesByDay[day]" :key="entry.id"
            class="bg-white p-2 rounded-lg border border-gray-200 flex items-center justify-between gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <div class="w-1 h-8 rounded-full shrink-0" :style="{ backgroundColor: getSubjectColor(entry.subjectName) }"></div>
              <div class="min-w-0">
                <div class="font-medium text-gray-800 text-xs truncate">{{ entry.subjectName || entry.customTitle || 'Untitled' }}</div>
                <div class="text-[10px] text-gray-500 truncate">{{ entry.classroomName }}</div>
              </div>
            </div>
            <div class="text-[10px] font-mono text-gray-500 shrink-0">
              {{ formatTime(entry.startTime) }} - {{ formatTime(entry.endTime) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="teacherEntries.length === 0" class="text-center py-8 bg-gray-50 rounded-xl">
        <i class="pi pi-calendar text-xl text-gray-300 mb-1.5 block"></i>
        <p class="text-gray-500 text-sm">This staff member has no classes scheduled.</p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useGetAllStaffQuery, useGetTimetablesQuery } from '@/types/vue-apollo-helpers';
import { ALL_DAYS } from '@/constants/timetable-presets';
import { teachableName } from '@/utils/teachable';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getTimeableLabel } from '@/utils/timetableLabel';

const selectedStaffId = ref<string | null>(null);
const { labels } = useSchoolLabels();
const { schoolType } = useSchoolFeatures();

const { result: staffResult } = useGetAllStaffQuery();
const { result: timetablesResult, loading: loadingTimetables } = useGetTimetablesQuery({ first: 200 });

const staffOptions = computed(() =>
  (staffResult.value?.getStaff?.data || []).map((s) => ({
    label: s.user?.name || 'Unknown',
    value: s.id,
  })),
);

interface FlatEntry {
  id: string;
  day: string;
  subjectName: string;
  customTitle: string;
  startTime: string;
  endTime: string;
  classroomName: string;
  slotId: string;
}

const teacherEntries = computed<FlatEntry[]>(() => {
  if (!selectedStaffId.value || !timetablesResult.value?.timetables?.data) return [];

  const entries: FlatEntry[] = [];
  for (const timetable of timetablesResult.value.timetables.data) {
    const classroomName = getTimeableLabel(timetable.timeable, {
      title: timetable.title,
      schoolType: schoolType.value,
      fallback: `Unnamed ${labels.value.CLASSROOM.toLowerCase()}`,
    });
    for (const entry of (timetable.entries || [])) {
      if (entry.staff?.id === selectedStaffId.value) {
        entries.push({
          id: entry.id,
          day: entry.day_of_week?.toUpperCase() || '',
          subjectName: teachableName(entry as Parameters<typeof teachableName>[0]),
          customTitle: entry.custom_title || '',
          startTime: entry.timeSlot?.start_time || '',
          endTime: entry.timeSlot?.end_time || '',
          classroomName,
          slotId: entry.timeSlot?.id || '',
        });
      }
    }
  }

  return entries.sort((a, b) => a.startTime.localeCompare(b.startTime));
});

const entriesByDay = computed(() => {
  const map: Record<string, FlatEntry[]> = {};
  for (const entry of teacherEntries.value) {
    if (!map[entry.day]) map[entry.day] = [];
    map[entry.day]!.push(entry);
  }
  return map;
});

const activeDays = computed(() =>
  ALL_DAYS.filter(d => entriesByDay.value[d]?.length),
);

const uniqueSubjects = computed(() =>
  [...new Set(teacherEntries.value.map(e => e.subjectName).filter(Boolean))],
);

const uniqueClassrooms = computed(() =>
  [...new Set(teacherEntries.value.map(e => e.classroomName).filter(Boolean))],
);

const conflicts = computed(() => {
  const messages: string[] = [];
  for (const day of ALL_DAYS) {
    const dayEntries = entriesByDay.value[day] || [];
    for (let i = 0; i < dayEntries.length; i++) {
      for (let j = i + 1; j < dayEntries.length; j++) {
        const a = dayEntries[i]!;
        const b = dayEntries[j]!;
        if (a.startTime < b.endTime && b.startTime < a.endTime) {
          messages.push(
            `${day}: ${a.subjectName || a.customTitle} (${a.classroomName}) overlaps with ${b.subjectName || b.customTitle} (${b.classroomName}) at ${formatTime(a.startTime)}`,
          );
        }
      }
    }
  }
  return messages;
});

function formatTime(time: string) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours || '0', 10);
  const m = minutes || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

function getSubjectColor(name: string) {
  if (!name) return '#ccc';
  const colors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
</script>
